import {
  formatReportCell,
  rowsOf,
  type QueryColumn,
  type ReportFormat,
  type ReportQuery,
  type WorkspaceFormats,
} from '@eis/core/reporting';
import type { ResultColumn, ResultRole } from '@eis/patterns/reporting';
import type { Bucket, FieldType, ReportDocument } from './document';

/**
 * From a query's result to what the renderers take (SDD-024 §7 step 7): the columns with their
 * roles, the rows as strings-or-null, and ONE cell formatter per report — the report's formats
 * (F-2) over the workspace defaults (F-1), a bucketed axis shown as its period, a `currencyField`
 * read from the row when the result projected it.
 */
export function resultColumnsOf(query: ReportQuery | undefined): ResultColumn[] {
  return (query?.columns ?? []).map((c: QueryColumn) => ({
    key: c.key ?? '',
    label: c.label ?? c.key ?? '',
    type: c.type ?? 'STRING',
    role: (c.role ?? 'FIELD') as ResultRole,
    index: c.index ?? 0,
  }));
}

export function resultRowsOf(query: ReportQuery | undefined): (string | null)[][] {
  return rowsOf(query?.rows);
}

/**
 * Whether a finished query has data (board 10's empty state): any row — but for a pivot, a row that
 * is not a subtotal. ROLLUP answers its grand-total row (`__grouping` ≠ 0) even over no input, so a
 * filter that matches nothing would otherwise draw a "Gesamt 0" grid instead of the empty state.
 */
export function hasResultData(
  query: ReportQuery | undefined,
  more: readonly (string | null)[][] = [],
): boolean {
  const rows = [...resultRowsOf(query), ...more];
  const at = resultColumnsOf(query).findIndex((c) => c.role === 'GROUPING');
  if (at < 0) return rows.length > 0;
  return rows.some((row) => Number(row[at] ?? '0') === 0);
}

/** The document field a result column stands for (rows/columns by level, values by key, table by field). */
export function fieldOfColumn(doc: ReportDocument, column: ResultColumn): string {
  switch (column.role) {
    case 'ROW':
      return doc.pivot?.rows?.[column.index]?.field ?? column.key;
    case 'COLUMN':
      return doc.pivot?.columns?.[column.index]?.field ?? column.key;
    default:
      return column.key;
  }
}

export function bucketOfColumn(doc: ReportDocument, column: ResultColumn): Bucket | undefined {
  if (column.role === 'ROW') return doc.pivot?.rows?.[column.index]?.bucket;
  if (column.role === 'COLUMN') return doc.pivot?.columns?.[column.index]?.bucket;
  return undefined;
}

/** The title of a column in the report: the document's label override (D-4), else the field. */
export function titleOfColumn(doc: ReportDocument, column: ResultColumn): string | undefined {
  switch (column.role) {
    case 'ROW':
      return doc.pivot?.rows?.[column.index]?.label;
    case 'COLUMN':
      return doc.pivot?.columns?.[column.index]?.label;
    case 'VALUE':
      return doc.pivot?.values[column.index]?.label ?? doc.formats?.[column.key]?.label;
    case 'FIELD':
      return (
        doc.table?.columns.find((c) => c.field === column.key)?.label ??
        doc.calculatedFields?.find((c) => c.name === column.key)?.label
      );
    default:
      return undefined;
  }
}

export type CellFormatter = (
  column: ResultColumn,
  raw: string | null,
  row?: readonly (string | null)[],
) => string;

export function cellFormatter(
  doc: ReportDocument,
  columns: readonly ResultColumn[],
  settings: WorkspaceFormats | undefined,
  locale: string,
): CellFormatter {
  return (column, raw, row) => {
    const field = fieldOfColumn(doc, column);
    const format: ReportFormat | undefined = doc.formats?.[column.key] ?? doc.formats?.[field];
    let rowCurrency: string | null | undefined;
    if (format?.currencyField !== undefined && row !== undefined) {
      const at = columns.findIndex((c) => c.key === format.currencyField);
      rowCurrency = at === -1 ? undefined : row[at];
    }
    const bucket = bucketOfColumn(doc, column);
    return formatReportCell(raw, {
      type: column.type as FieldType,
      ...(format === undefined ? {} : { format }),
      ...(settings === undefined ? {} : { settings }),
      ...(rowCurrency === undefined || rowCurrency === null ? {} : { rowCurrency }),
      ...(bucket === undefined ? {} : { bucket }),
      locale,
    });
  };
}

/** The workspace settings document as the formatter reads it (falls back to the defaults). */
export function workspaceFormatsOf(
  document: Record<string, unknown> | undefined,
): WorkspaceFormats | undefined {
  if (document === undefined || typeof document.formats !== 'object' || document.formats === null)
    return undefined;
  return document as unknown as WorkspaceFormats;
}
